import { useState, useEffect, useCallback } from "react";
import { 
  FileText, Download, Search, Building2, Calendar, User,
  CheckCircle2, XCircle, Clock, DollarSign, Eye, Filter,
  ThumbsUp, ThumbsDown, AlertCircle, Send, RefreshCw, Plus, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

const QUOTE_STATUS_CONFIG = {
  pending: { label: "Pendiente", color: "bg-amber-100 text-amber-800", icon: Clock },
  accepted: { label: "Aceptada", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-800", icon: XCircle },
  expired: { label: "Vencida", color: "bg-gray-100 text-gray-800", icon: Clock },
};

const SERVICE_TYPES = [
  "Permisos de Autoconsumo",
  "Permisos de Comercializacion",
  "Controles Volumetricos", 
  "Gestion Integral",
  "Suministro de Combustible"
];

export default function CotizacionesTab() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  
  // Accept modal state
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptServiceType, setAcceptServiceType] = useState("");
  const [accepting, setAccepting] = useState(false);
  
  // Recotizar modal state
  const [recotizarModalOpen, setRecotizarModalOpen] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [recotizarForm, setRecotizarForm] = useState({
    items: [],
    notes: "",
    validity_days: 7,
  });
  const [newItem, setNewItem] = useState({
    service_id: "",
    service_name: "",
    description: "",
    quantity: 1,
    unit_price: 0,
  });
  const [creatingQuote, setCreatingQuote] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Export Excel
  const [exporting, setExporting] = useState(false);

  const fetchQuotations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quotations`, axCfg);
      // Sort by date descending
      const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setQuotations(sorted);
    } catch (e) {
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchQuotations();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchQuotations]);

  // Filter quotations
  const filteredQuotations = quotations.filter(q => {
    if (filterStatus !== "all" && q.status !== filterStatus) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      q.quotation_number?.toLowerCase().includes(searchLower) ||
      q.prospect_name?.toLowerCase().includes(searchLower) ||
      q.prospect_company?.toLowerCase().includes(searchLower) ||
      q.prospect_email?.toLowerCase().includes(searchLower)
    );
  });

  const downloadPdf = async (quotationId) => {
    try {
      const response = await axios.get(
        `${API}/admin/quotations/${quotationId}/pdf`,
        { ...axCfg, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cotizacion_${quotationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error al descargar PDF");
    }
  };

  const openDetailModal = (quotation) => {
    setSelectedQuotation(quotation);
    setDetailModalOpen(true);
  };

  const openRejectModal = (quotation) => {
    setSelectedQuotation(quotation);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const openAcceptModal = (quotation) => {
    setSelectedQuotation(quotation);
    setAcceptServiceType("Permisos de Autoconsumo");
    setAcceptModalOpen(true);
  };

  const openDeleteModal = (quotation) => {
    setQuotationToDelete(quotation);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!quotationToDelete) return;
    setDeleting(true);
    try {
      await axios.patch(`${API}/admin/quotations/${quotationToDelete.id}/delete`, {}, axCfg);
      toast.success("Cotización eliminada correctamente");
      setDeleteModalOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar cotización");
    } finally {
      setDeleting(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/quotations/export/all`, axCfg);
      const data = res.data;
      
      // Convert to CSV
      if (data.length === 0) {
        toast.warning("No hay cotizaciones para exportar");
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(","),
        ...data.map(row => headers.map(h => {
          const val = row[h] ?? "";
          // Escape commas and quotes
          return typeof val === "string" && (val.includes(",") || val.includes('"')) 
            ? `"${val.replace(/"/g, '""')}"` 
            : val;
        }).join(","))
      ].join("\n");
      
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cotizaciones_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Exportación completada");
    } catch (e) {
      toast.error("Error al exportar cotizaciones");
    } finally {
      setExporting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("El motivo de rechazo es obligatorio");
      return;
    }

    setRejecting(true);
    try {
      const formData = new FormData();
      formData.append("status", "rejected");
      formData.append("rejection_reason", rejectReason);
      
      await axios.patch(
        `${API}/admin/quotations/${selectedQuotation.id}/status`,
        formData,
        { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Cotización rechazada");
      setRejectModalOpen(false);
      fetchQuotations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al rechazar cotización");
    } finally {
      setRejecting(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const formData = new FormData();
      // El service_type se toma de la cotización en el backend
      formData.append("notes", "");
      
      const response = await axios.post(
        `${API}/admin/quotations/${selectedQuotation.id}/accept`,
        formData,
        { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      if (response.data.client_created) {
        toast.success("Cotización aceptada. Cliente creado automáticamente. Orden de Servicio generada.");
      } else {
        toast.success("Cotización aceptada. Orden de Servicio generada.");
      }
      
      setAcceptModalOpen(false);
      fetchQuotations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aceptar cotización");
    } finally {
      setAccepting(false);
    }
  };

  const handleRecotizar = async (quotation) => {
    setSelectedQuotation(quotation);
    // Copy items from previous quotation
    setRecotizarForm({
      items: quotation.items?.map(item => ({...item})) || [],
      notes: `Nueva cotización basada en ${quotation.quotation_number} (rechazada). ${quotation.notes || ""}`,
      validity_days: 7,
    });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
    
    // Fetch service catalog
    try {
      const res = await axios.get(`${API}/admin/service-catalog`, axCfg);
      setServiceCatalog(res.data);
    } catch (e) {
      console.error("Error loading catalog");
    }
    
    setRecotizarModalOpen(true);
  };

  const handleServiceSelect = (serviceId) => {
    const service = serviceCatalog.find(s => s.id === serviceId);
    if (service) {
      setNewItem({
        service_id: service.id,
        service_name: service.name,
        description: service.description,
        quantity: 1,
        unit_price: service.price,
      });
    }
  };

  const addItemToRecotizar = () => {
    if (!newItem.service_name || newItem.unit_price <= 0) {
      toast.error("Completa los datos del servicio");
      return;
    }
    setRecotizarForm({
      ...recotizarForm,
      items: [...recotizarForm.items, { ...newItem }],
    });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
  };

  const removeItemFromRecotizar = (index) => {
    setRecotizarForm({
      ...recotizarForm,
      items: recotizarForm.items.filter((_, i) => i !== index),
    });
  };

  const calculateRecotizarTotals = () => {
    const subtotal = recotizarForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const iva = subtotal * 0.16;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const submitRecotizar = async () => {
    if (recotizarForm.items.length === 0) {
      toast.error("Agrega al menos un servicio");
      return;
    }

    setCreatingQuote(true);
    try {
      await axios.post(`${API}/admin/quotations`, {
        prospect_id: selectedQuotation.prospect_id,
        items: recotizarForm.items,
        notes: recotizarForm.notes,
        validity_days: recotizarForm.validity_days,
      }, axCfg);
      
      toast.success("Nueva cotización creada correctamente");
      setRecotizarModalOpen(false);
      fetchQuotations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cotización");
    } finally {
      setCreatingQuote(false);
    }
  };

  // Stats
  const totalQuotations = quotations.length;
  const pendingQuotations = quotations.filter(q => q.status === "pending").length;
  const acceptedQuotations = quotations.filter(q => q.status === "accepted").length;
  const rejectedQuotations = quotations.filter(q => q.status === "rejected").length;
  const totalValue = quotations.reduce((sum, q) => sum + (q.total || 0), 0);
  const acceptedValue = quotations.filter(q => q.status === "accepted").reduce((sum, q) => sum + (q.total || 0), 0);

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando cotizaciones...</p>;

  return (
    <div className="space-y-6">
      {/* Stats Cards - Clickeable para filtrar */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterStatus === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalQuotations}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterStatus === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingQuotations}</p>
                <p className="text-xs text-gray-600">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterStatus === 'accepted' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilterStatus('accepted')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{acceptedQuotations}</p>
                <p className="text-xs text-gray-600">Aceptadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-none shadow-sm cursor-pointer transition-all hover:scale-105 ${filterStatus === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{rejectedQuotations}</p>
                <p className="text-xs text-gray-600">Rechazadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold">${(totalValue/1000).toFixed(0)}k</p>
                <p className="text-xs text-gray-600">Total Cotizado</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#103F38] flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold">${(acceptedValue/1000).toFixed(0)}k</p>
                <p className="text-xs text-gray-600">Ganado</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por folio, cliente, empresa..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-cotizaciones"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="accepted">Aceptadas</SelectItem>
                <SelectItem value="rejected">Rechazadas</SelectItem>
                <SelectItem value="expired">Vencidas</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500">{filteredQuotations.length} cotizaciones</span>
            <Button
              size="sm"
              variant="outline"
              onClick={exportToExcel}
              disabled={exporting}
              className="ml-2"
              title="Exportar todas las cotizaciones a Excel (incluyendo eliminadas)"
              data-testid="export-quotations-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              {exporting ? "Exportando..." : "Exportar Excel"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quotations List */}
      {filteredQuotations.length === 0 ? (
        <Card className="border-none">
          <CardContent className="text-center py-16">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay cotizaciones {search ? "con ese criterio" : ""}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-none shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Prospectos Cotizados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Folio</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Cliente / Empresa</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo de Trámite</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Vigencia</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Total</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Estado</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotations.map((q) => {
                    const statusCfg = QUOTE_STATUS_CONFIG[q.status] || QUOTE_STATUS_CONFIG.pending;
                    const StatusIcon = statusCfg.icon;
                    const isExpired = new Date(q.validity_date) < new Date() && q.status === "pending";
                    const isPending = q.status === "pending" && !isExpired;
                    
                    return (
                      <tr 
                        key={q.id} 
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid={`quotation-row-${q.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[#103F38] border-[#103F38] text-[10px]">
                              COTIZADO
                            </Badge>
                            <span className="font-mono font-medium text-[#103F38]">{q.quotation_number}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{q.prospect_name}</p>
                            <p className="text-xs text-gray-500">{q.prospect_company || "-"}</p>
                            <p className="text-xs text-gray-400">{q.prospect_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            {q.service_type || "Sin definir"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(q.created_at).toLocaleDateString('es-MX')}
                        </td>
                        <td className="py-3 px-4">
                          <span className={isExpired ? "text-red-600 font-medium" : "text-gray-600"}>
                            {new Date(q.validity_date).toLocaleDateString('es-MX')}
                            {isExpired && <span className="block text-[10px]">VENCIDA</span>}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-bold">
                          ${q.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={statusCfg.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {isExpired ? "Vencida" : statusCfg.label}
                          </Badge>
                          {q.status === "rejected" && q.rejection_reason && (
                            <p className="text-[10px] text-red-600 mt-1 max-w-[150px] truncate" title={q.rejection_reason}>
                              {q.rejection_reason}
                            </p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            {isPending && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 h-7 px-2"
                                  onClick={() => openAcceptModal(q)}
                                  title="Aprobar cotización"
                                >
                                  <ThumbsUp className="h-3 w-3 mr-1" />
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2"
                                  onClick={() => openRejectModal(q)}
                                  title="Rechazar cotización"
                                >
                                  <ThumbsDown className="h-3 w-3 mr-1" />
                                  Rechazar
                                </Button>
                              </>
                            )}
                            {q.status === "rejected" && (
                              <Button
                                size="sm"
                                className="bg-amber-500 hover:bg-amber-600 h-7 px-2"
                                onClick={() => handleRecotizar(q)}
                                title="Crear nueva cotización"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Recotizar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDetailModal(q)}
                              title="Ver detalles"
                              className="h-7 px-2"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadPdf(q.id)}
                              title="Descargar PDF"
                              className="h-7 px-2"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteModal(q)}
                              title="Eliminar cotización"
                              className="h-7 px-2 text-gray-400 hover:text-red-600"
                              data-testid={`delete-quotation-${q.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#103F38]" />
              Detalle de Cotizacion {selectedQuotation?.quotation_number}
            </DialogTitle>
          </DialogHeader>

          {selectedQuotation && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[#103F38] border-[#103F38]">
                  COTIZADO
                </Badge>
                <Badge className={QUOTE_STATUS_CONFIG[selectedQuotation.status]?.color}>
                  {QUOTE_STATUS_CONFIG[selectedQuotation.status]?.label}
                </Badge>
              </div>

              {/* Rejection Reason */}
              {selectedQuotation.status === "rejected" && selectedQuotation.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800">Motivo de Rechazo:</p>
                  <p className="text-sm text-red-700">{selectedQuotation.rejection_reason}</p>
                </div>
              )}

              {/* Client Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Cliente</p>
                    <p className="font-medium">{selectedQuotation.prospect_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Empresa</p>
                    <p className="font-medium">{selectedQuotation.prospect_company || "-"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{selectedQuotation.prospect_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fecha</p>
                    <p className="font-medium">{new Date(selectedQuotation.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#103F38] text-white">
                    <tr>
                      <th className="text-left py-2 px-3">Servicio</th>
                      <th className="text-center py-2 px-3">Cant.</th>
                      <th className="text-right py-2 px-3">P. Unit.</th>
                      <th className="text-right py-2 px-3">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuotation.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-2 px-3">
                          <p className="font-medium">{item.service_name}</p>
                          {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                        </td>
                        <td className="py-2 px-3 text-center">{item.quantity}</td>
                        <td className="py-2 px-3 text-right">${item.unit_price?.toLocaleString('es-MX')}</td>
                        <td className="py-2 px-3 text-right font-medium">
                          ${(item.quantity * item.unit_price)?.toLocaleString('es-MX')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-[#103F38]/5 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${selectedQuotation.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>IVA (16%):</span>
                  <span>${selectedQuotation.iva?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-[#103F38] border-t pt-2">
                  <span>TOTAL:</span>
                  <span>${selectedQuotation.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => downloadPdf(selectedQuotation.id)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDetailModalOpen(false)}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Rechazar Cotizacion
            </DialogTitle>
            <DialogDescription>
              {selectedQuotation?.quotation_number} - ${selectedQuotation?.total?.toLocaleString('es-MX')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                El motivo de rechazo es <strong>obligatorio</strong> y será registrado en el historial del prospecto.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rejection_reason">Motivo de Rechazo *</Label>
              <Textarea
                id="rejection_reason"
                placeholder="Ej: Precio fuera de presupuesto, decidió no proceder, eligió otro proveedor..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="rejection-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
            >
              {rejecting ? "Rechazando..." : "Confirmar Rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept Modal */}
      <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Aprobar Cotización
            </DialogTitle>
            <DialogDescription>
              {selectedQuotation?.quotation_number} - ${selectedQuotation?.total?.toLocaleString('es-MX')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-800">
                Al aprobar esta cotización:
              </p>
              <ul className="text-sm text-green-700 list-disc ml-4 mt-1">
                <li>Se creará automáticamente el <strong>Cliente</strong></li>
                <li>Se generará una <strong>Orden de Servicio</strong> (Pre-trámite)</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-xs text-blue-600 font-medium">Tipo de Trámite (definido en prospecto)</Label>
                <p className="text-lg font-bold text-blue-800 mt-1">
                  {selectedQuotation?.service_type || "Sin definir"}
                </p>
              </div>
              
              {!selectedQuotation?.service_type && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ Esta cotización no tiene tipo de trámite definido. Se usará "Permisos de Autoconsumo" por defecto.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? "Procesando..." : "Confirmar y Crear Cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recotizar Modal */}
      <Dialog open={recotizarModalOpen} onOpenChange={setRecotizarModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="h-5 w-5" />
              Recotizar - Nueva Propuesta
            </DialogTitle>
            <DialogDescription>
              {selectedQuotation?.prospect_name} | {selectedQuotation?.prospect_company}
              <br />
              <span className="text-red-500 text-xs">
                Cotización anterior rechazada: {selectedQuotation?.quotation_number} - Motivo: {selectedQuotation?.rejection_reason}
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* Add Service Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h4 className="font-medium text-sm mb-3">Agregar Servicio</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Servicio del Catalogo</Label>
                <Select
                  value={newItem.service_id}
                  onValueChange={handleServiceSelect}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar servicio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCatalog.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} - ${s.price?.toLocaleString('es-MX')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">O escribe manualmente</Label>
                <Input
                  className="mt-1"
                  placeholder="Nombre del servicio"
                  value={newItem.service_name}
                  onChange={(e) => setNewItem({ ...newItem, service_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="1"
                  className="mt-1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-xs">Precio Unitario</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-1"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={addItemToRecotizar}
                  className="w-full bg-[#103F38] hover:bg-[#0c332d]"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar
                </Button>
              </div>
            </div>
          </div>

          {/* Items List */}
          {recotizarForm.items.length > 0 && (
            <div className="mb-4">
              <Label className="text-xs mb-2 block">Servicios en la nueva cotización</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Servicio</th>
                      <th className="p-2 text-center">Cant.</th>
                      <th className="p-2 text-right">P.Unit</th>
                      <th className="p-2 text-right">Importe</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recotizarForm.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          <div className="font-medium">{item.service_name}</div>
                        </td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-right">${item.unit_price?.toLocaleString('es-MX')}</td>
                        <td className="p-2 text-right font-medium">
                          ${(item.quantity * item.unit_price)?.toLocaleString('es-MX')}
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500"
                            onClick={() => removeItemFromRecotizar(idx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              {(() => {
                const { subtotal, iva, total } = calculateRecotizarTotals();
                return (
                  <div className="mt-3 bg-[#103F38]/5 p-3 rounded-lg">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Subtotal:</span>
                      <span>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>IVA (16%):</span>
                      <span>${iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#103F38]">
                      <span>TOTAL:</span>
                      <span>${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Notes and Validity */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs">Validez (días)</Label>
              <Select
                value={String(recotizarForm.validity_days)}
                onValueChange={(v) => setRecotizarForm({ ...recotizarForm, validity_days: parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 días</SelectItem>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="15">15 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea
                className="mt-1"
                rows={2}
                placeholder="Notas adicionales..."
                value={recotizarForm.notes}
                onChange={(e) => setRecotizarForm({ ...recotizarForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecotizarModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={submitRecotizar}
              disabled={creatingQuote || recotizarForm.items.length === 0}
            >
              {creatingQuote ? "Creando..." : "Crear Nueva Cotización"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Eliminar Cotización
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar esta cotización?
            </DialogDescription>
          </DialogHeader>

          {quotationToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="font-mono font-bold text-[#103F38]">{quotationToDelete.quotation_number}</p>
                <p className="text-sm font-medium">{quotationToDelete.prospect_name}</p>
                <p className="text-sm text-gray-500">{quotationToDelete.prospect_company}</p>
                <p className="text-sm font-bold mt-2">Total: ${quotationToDelete.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> La cotización se ocultará de la lista pero se mantendrá en los registros para auditoría y podrá ser incluida en la exportación a Excel.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteModalOpen(false);
                setQuotationToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Eliminando..." : "Sí, Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
